import { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Camera, PaperPlaneTilt } from "@phosphor-icons/react";

export default function CheckinForm() {
  const [feedback, setFeedback] = useState("");
  const [weight, setWeight] = useState("");
  const [energy, setEnergy] = useState(3);
  const [beforePhotoId, setBeforePhotoId] = useState(null);
  const [afterPhotoId, setAfterPhotoId] = useState(null);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => api.get("/portal/checkins").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const uploadPhoto = async (slot, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingSlot(slot);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (slot === "before") setBeforePhotoId(data.file_id);
      else setAfterPhotoId(data.file_id);
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploadingSlot(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/portal/checkin", {
        feedback,
        weight: weight ? parseFloat(weight) : null,
        energy_level: energy,
        photo_before_file_id: beforePhotoId,
        photo_after_file_id: afterPhotoId,
      });
      toast.success("Check-in submitted!");
      setFeedback("");
      setWeight("");
      setBeforePhotoId(null);
      setAfterPhotoId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkin-form-page">
      <h1 className="font-heading text-3xl text-white tracking-tight mb-6">Weekly Check-in</h1>
      <form onSubmit={submit} className="bg-[#141414] border border-[#27272A] rounded-2xl p-6 space-y-4 mb-10" data-testid="checkin-form">
        <textarea
          data-testid="checkin-feedback-input"
          required
          placeholder="How did this week go? Energy, soreness, wins, struggles..."
          rows={4}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A]"
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            data-testid="checkin-weight-input"
            type="number"
            placeholder="Current weight (lbs)"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A]"
          />
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#71717A] whitespace-nowrap">Energy 1-5</span>
            <input
              data-testid="checkin-energy-input"
              type="range"
              min="1"
              max="5"
              value={energy}
              onChange={(e) => setEnergy(parseInt(e.target.value))}
              className="flex-1 accent-[#D4AF37]"
            />
            <span className="text-white font-semibold">{energy}</span>
          </div>
        </div>
        <div>
          <p className="text-xs text-[#71717A] mb-2">Progress photos (optional)</p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { slot: "before", label: "Before", id: beforePhotoId },
              { slot: "after", label: "After", id: afterPhotoId },
            ].map(({ slot, label, id }) => (
              <label
                key={slot}
                data-testid={`checkin-photo-upload-label-${slot}`}
                className="relative flex flex-col items-center justify-center gap-2 bg-[#0A0A0A] border border-dashed border-[#27272A] hover:border-[#D4AF37]/60 rounded-xl aspect-square cursor-pointer transition-colors overflow-hidden"
              >
                {id ? (
                  <img src={fileUrl(id)} alt={label} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <>
                    <Camera size={22} className="text-[#71717A]" />
                    <span className="text-xs text-[#71717A]">{uploadingSlot === slot ? "Uploading..." : `Add ${label}`}</span>
                  </>
                )}
                {id && (
                  <span className="absolute top-1.5 left-1.5 bg-black/70 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">
                    {label}
                  </span>
                )}
                <input
                  data-testid={`checkin-photo-upload-input-${slot}`}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadPhoto(slot, e)}
                />
              </label>
            ))}
          </div>
        </div>
        <button
          data-testid="checkin-submit-button"
          disabled={submitting || !feedback}
          type="submit"
          className="flex items-center gap-2 bg-[#D4AF37] disabled:opacity-40 text-black font-semibold px-6 py-3 rounded-full"
        >
          <PaperPlaneTilt size={16} /> {submitting ? "Submitting..." : "Submit Check-in"}
        </button>
      </form>
      <h2 className="font-heading text-xl text-white tracking-tight mb-4">Past Check-ins</h2>
      <div className="space-y-3">
        {history.map((h) => (
          <div key={h.id} data-testid={`checkin-history-${h.id}`} className="border border-[#27272A] rounded-xl p-4">
            <p className="text-xs text-[#71717A] mb-1">{new Date(h.created_at).toLocaleDateString()}</p>
            <p className="text-sm text-[#D4D4D8] mb-2">{h.feedback}</p>
            {(h.photo_before_file_id || h.photo_after_file_id) && (
              <div className="flex gap-3">
                {h.photo_before_file_id && (
                  <div className="relative">
                    <img src={fileUrl(h.photo_before_file_id)} alt="before" className="w-16 h-16 rounded-lg object-cover" />
                    <span className="absolute top-1 left-1 bg-black/70 text-[#D4AF37] text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full">Before</span>
                  </div>
                )}
                {h.photo_after_file_id && (
                  <div className="relative">
                    <img src={fileUrl(h.photo_after_file_id)} alt="after" className="w-16 h-16 rounded-lg object-cover" />
                    <span className="absolute top-1 left-1 bg-black/70 text-[#D4AF37] text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full">After</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!history.length && <p className="text-[#71717A]">No check-ins yet.</p>}
      </div>
    </div>
  );
}
